import {
  FC,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import { KTSVG } from "../../../../_metronic/helpers";
import {
  CardapioItem,
  CardapioPedido,
  CARDAPIO_PEDIDO_STATUS,
  ClienteAutocomplete,
  appendCardapioPedidoItems,
  clienteOptionLabel,
  clienteWristbandKey,
  createCardapioPedido,
  fetchCardapioItens,
  fetchCardapioPedidos,
} from "../utils/cardapio.api";
import {
  CaixaEmployee,
  fetchEmployees,
} from "../../caixa-financeiro/utils/caixa.api";
import { getLoggedUserIdentity } from "../../../utils/loggedUser";
import {
  formatCurrency,
  effectiveCommissionPercent,
  DEFAULT_CARDAPIO_COMMISSION_PERCENT,
  cardapioItemOptionLabel,
  latestPedidoByWristband,
} from "../utils/cardapio.utils";
import { ClienteAutocompleteField } from "./ClienteAutocompleteField";

export interface NovoPedidoCartLine {
  _id_cardapio_item: number;
  item_name: string;
  quantity: number;
  unit_price: number;
}

export interface NovoPedidoDraft {
  waiterName: string;
  wristband: string;
  commissionPercent: number | "";
  cart: NovoPedidoCartLine[];
}

interface Props {
  /** When false, form stays idle (e.g. closed modal). Default true. */
  active?: boolean;
  idPrefix?: string;
  showCancel?: boolean;
  onCancel?: () => void;
  onSuccess?: () => void;
  onDraftChange?: (draft: NovoPedidoDraft) => void;
}

export const NovoPedidoForm: FC<Props> = ({
  active = true,
  idPrefix = "pedido",
  showCancel = false,
  onCancel,
  onSuccess,
  onDraftChange,
}) => {
  const [employees, setEmployees] = useState<CaixaEmployee[]>([]);
  const [vendedorName, setVendedorName] = useState("");
  const [vendedorId, setVendedorId] = useState<number | null>(null);
  const [commissionPercent, setCommissionPercent] = useState<number | "">(
    DEFAULT_CARDAPIO_COMMISSION_PERCENT,
  );
  const [selectedCliente, setSelectedCliente] =
    useState<ClienteAutocomplete | null>(null);

  const [itemQuery, setItemQuery] = useState("");
  const [itemOptions, setItemOptions] = useState<CardapioItem[]>([]);
  const [itemLoading, setItemLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState<CardapioItem | null>(null);
  const [showItemDrop, setShowItemDrop] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [cart, setCart] = useState<NovoPedidoCartLine[]>([]);
  const [saving, setSaving] = useState(false);
  const [openPedidos, setOpenPedidos] = useState<CardapioPedido[]>([]);

  const itemTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const wristband = selectedCliente
    ? clienteWristbandKey(selectedCliente)
    : "";

  const openPedidoForWristband = latestPedidoByWristband(openPedidos, wristband);

  const draftTotal = cart.reduce(
    (sum, line) => sum + line.unit_price * line.quantity,
    0,
  );

  const resetForm = useCallback(() => {
    setCommissionPercent(DEFAULT_CARDAPIO_COMMISSION_PERCENT);
    setSelectedCliente(null);
    setItemQuery("");
    setItemOptions([]);
    setSelectedItem(null);
    setShowItemDrop(false);
    setQuantity(1);
    setCart([]);
  }, []);

  useEffect(() => {
    if (!active) return;

    resetForm();
    fetchEmployees()
      .then(setEmployees)
      .catch(() => setEmployees([]));
    fetchCardapioPedidos(undefined, CARDAPIO_PEDIDO_STATUS.EM_ABERTO)
      .then(setOpenPedidos)
      .catch(() => setOpenPedidos([]));

    void Promise.resolve(getLoggedUserIdentity())
      .then((logged) => {
        if (!logged) {
          setVendedorName("");
          setVendedorId(null);
          return;
        }
        setVendedorName(logged.name);
        setVendedorId(logged.id);
      })
      .catch(() => {
        setVendedorName("");
        setVendedorId(null);
      });
  }, [active, resetForm]);

  const onDraftChangeRef = useRef(onDraftChange);
  onDraftChangeRef.current = onDraftChange;

  useEffect(() => {
    onDraftChangeRef.current?.({
      waiterName: vendedorName,
      wristband,
      commissionPercent,
      cart,
    });
  }, [vendedorName, wristband, commissionPercent, cart]);

  useEffect(() => {
    if (!active) return;
    if (itemTimer.current) clearTimeout(itemTimer.current);
    const term = itemQuery.trim();
    if (selectedItem && cardapioItemOptionLabel(selectedItem) === itemQuery) {
      setItemOptions([]);
      return;
    }
    if (term.length < 1) {
      setItemOptions([]);
      setItemLoading(false);
      return;
    }
    setItemLoading(true);
    itemTimer.current = setTimeout(() => {
      fetchCardapioItens(term)
        .then((list) => {
          setItemOptions(list);
          setShowItemDrop(true);
        })
        .catch(() => setItemOptions([]))
        .finally(() => setItemLoading(false));
    }, 300);
    return () => {
      if (itemTimer.current) clearTimeout(itemTimer.current);
    };
  }, [itemQuery, selectedItem, active]);

  const resolveVendedor = async (): Promise<{
    employee: CaixaEmployee;
    name: string;
  } | null> => {
    const name = vendedorName.trim();
    if (!name) {
      Swal.fire(
        "Atenção",
        "Não foi possível identificar o vendedor logado. Faça login novamente.",
        "warning",
      );
      return null;
    }
    const matched =
      (vendedorId && employees.find((e) => e._id === vendedorId)) ||
      employees.find(
        (e) => e.name.trim().toLowerCase() === name.toLowerCase(),
      );
    if (matched) {
      return { employee: matched, name: matched.name };
    }
    return {
      employee: { _id: vendedorId || 0, name, login: "" },
      name,
    };
  };

  const addToCart = () => {
    if (!selectedItem) {
      Swal.fire("Atenção", "Selecione um item a ser vendido.", "warning");
      return;
    }
    if (!quantity || quantity < 1) {
      Swal.fire("Atenção", "Informe uma quantidade válida.", "warning");
      return;
    }
    const item = selectedItem;
    setCart((prev) => {
      const existing = prev.find((l) => l._id_cardapio_item === item._id);
      if (existing) {
        return prev.map((l) =>
          l._id_cardapio_item === item._id
            ? { ...l, quantity: l.quantity + quantity }
            : l,
        );
      }
      return [
        ...prev,
        {
          _id_cardapio_item: item._id,
          item_name: cardapioItemOptionLabel(item),
          quantity,
          unit_price: item.price,
        },
      ];
    });
    setSelectedItem(null);
    setItemQuery("");
    setItemOptions([]);
    setQuantity(1);
  };

  const removeLine = (id: number) => {
    setCart((prev) => prev.filter((l) => l._id_cardapio_item !== id));
  };

  const handleConfirm = async () => {
    const waiter = await resolveVendedor();
    if (!waiter) return;
    if (!selectedCliente || !wristband.trim()) {
      Swal.fire(
        "Atenção",
        "Selecione o cliente (Nome/CPF/Pulseira).",
        "warning",
      );
      return;
    }
    if (!cart.length) {
      Swal.fire("Atenção", "Adicione ao menos um item ao pedido.", "warning");
      return;
    }

    setSaving(true);
    const trimmedWristband = wristband.trim();
    const cartItems = cart.map((line) => ({
      _id_cardapio_item: line._id_cardapio_item,
      quantity: line.quantity,
    }));
    const existingPedido = latestPedidoByWristband(openPedidos, trimmedWristband);
    const clientNote = `Cliente: ${clienteOptionLabel(selectedCliente)}`;

    try {
      if (existingPedido) {
        await appendCardapioPedidoItems(existingPedido._id, cartItems);
        Swal.fire(
          "Sucesso",
          `Itens adicionados ao pedido em aberto do cliente ${selectedCliente.nome}.`,
          "success",
        );
      } else {
        await createCardapioPedido({
          waiter_name: waiter.name,
          seller_name: waiter.name,
          wristband_number: trimmedWristband,
          commission_percent: effectiveCommissionPercent(commissionPercent),
          notes: clientNote,
          _id_usuario: selectedCliente.cliente_id,
          cliente_id: selectedCliente.cliente_id,
          status: CARDAPIO_PEDIDO_STATUS.EM_ABERTO,
          items: cartItems,
        });
        Swal.fire("Sucesso", "Pedido registrado com sucesso!", "success");
      }
      window.dispatchEvent(new Event("cardapio:pedido-changed"));
      window.dispatchEvent(new Event("caixa:session-changed"));
      resetForm();
      onSuccess?.();
    } catch {
      Swal.fire(
        "Erro",
        existingPedido
          ? "Não foi possível adicionar itens ao pedido em aberto."
          : "Não foi possível registrar o pedido.",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  if (!active) return null;

  return (
    <>
      <div className="row g-3 mb-6 align-items-end">
        <div className="col-12 col-md-3">
          <label className="form-label required" htmlFor={`${idPrefix}-vendedor`}>
            Vendedor
          </label>
          <input
            id={`${idPrefix}-vendedor`}
            type="text"
            className="form-control form-control-sm"
            value={vendedorName || "Carregando..."}
            readOnly
            disabled
          />
        </div>
        <div className="col-12 col-md-8">
          <ClienteAutocompleteField
            id={`${idPrefix}-cliente`}
            value={selectedCliente}
            onChange={setSelectedCliente}
            hint={
              openPedidoForWristband && selectedCliente
                ? `Cliente com pedido em aberto — novos itens serão adicionados ao pedido #${openPedidoForWristband._id}.`
                : undefined
            }
          />
        </div>
        <div className="col-12 col-md-1">
          <label className="form-label" htmlFor={`${idPrefix}-comissao`}>
            Comissão (%)
          </label>
          <input
            id={`${idPrefix}-comissao`}
            type="number"
            min={0}
            max={100}
            step="0.01"
            className="form-control form-control-sm"
            value={commissionPercent}
            onChange={(e) =>
              setCommissionPercent(
                e.target.value === "" ? "" : Number(e.target.value),
              )
            }
            placeholder="0"
          />
        </div>
      </div>

      <div className="separator my-6" />

      <div className="row g-3 align-items-end mb-5">
        <div className="col-lg-7 position-relative">
          <label className="form-label" htmlFor={`${idPrefix}-item`}>
            Item a ser vendido
          </label>
          <input
            id={`${idPrefix}-item`}
            type="text"
            className="form-control form-control-sm"
            value={itemQuery}
            autoComplete="off"
            placeholder="Digite o nome do item..."
            onFocus={() => {
              if (itemOptions.length) setShowItemDrop(true);
            }}
            onBlur={() => {
              setTimeout(() => setShowItemDrop(false), 150);
            }}
            onChange={(e) => {
              setItemQuery(e.target.value);
              setSelectedItem(null);
            }}
          />
          {itemLoading && <div className="form-text">Buscando itens...</div>}
          {showItemDrop && itemOptions.length > 0 && (
            <ul
              className="list-group position-absolute w-100 shadow-sm"
              style={{ zIndex: 1055, maxHeight: 220, overflowY: "auto" }}
            >
              {itemOptions.map((item) => (
                <li key={item._id}>
                  <button
                    type="button"
                    className="list-group-item list-group-item-action text-start"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      setSelectedItem(item);
                      setItemQuery(cardapioItemOptionLabel(item));
                      setShowItemDrop(false);
                      setItemOptions([]);
                    }}
                  >
                    {cardapioItemOptionLabel(item)} -{" "}
                    {formatCurrency(item.price)}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {!itemLoading &&
            itemQuery.trim() &&
            itemOptions.length === 0 &&
            !selectedItem && (
              <div className="form-text">
                Nenhum item encontrado. Cadastre em{" "}
                <Link to="/cardapio/create">Cardápio</Link>.
              </div>
            )}
        </div>
        <div className="col-lg-3">
          <label className="form-label" htmlFor={`${idPrefix}-qtd`}>
            Qtd
          </label>
          <input
            id={`${idPrefix}-qtd`}
            type="number"
            min={1}
            className="form-control form-control-sm"
            value={quantity}
            onChange={(e) => setQuantity(parseInt(e.target.value, 10) || 1)}
          />
        </div>
        <div className="col-lg-2">
          <button
            type="button"
            className="btn btn-primary btn-sm w-100"
            onClick={addToCart}
          >
            Adicionar
          </button>
        </div>
      </div>

      <div className="table-responsive">
        <table className="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-3">
          <thead>
            <tr className="fw-bold text-muted">
              <th>Item</th>
              <th className="text-end">Qtd</th>
              <th className="text-end">Valor Unit.</th>
              <th className="text-end">Total</th>
              <th className="text-end">Ações</th>
            </tr>
          </thead>
          <tbody>
            {cart.length === 0 ? (
              <tr>
                <td colSpan={5} className="text-center text-muted py-6">
                  Nenhum item adicionado ao pedido.
                </td>
              </tr>
            ) : (
              cart.map((line) => (
                <tr key={line._id_cardapio_item}>
                  <td className="text-gray-800 fw-semibold">{line.item_name}</td>
                  <td className="text-end">{line.quantity}</td>
                  <td className="text-end">{formatCurrency(line.unit_price)}</td>
                  <td className="text-end">
                    {formatCurrency(line.unit_price * line.quantity)}
                  </td>
                  <td className="text-end">
                    <button
                      type="button"
                      className="btn btn-icon btn-bg-light btn-active-color-danger btn-sm"
                      title="Remover"
                      onClick={() => removeLine(line._id_cardapio_item)}
                    >
                      <KTSVG
                        path="/media/icons/duotune/general/gen027.svg"
                        className="svg-icon-3"
                      />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="d-flex justify-content-end fs-4 fw-bold mt-3">
        Total:&nbsp;
        <span className="text-primary">{formatCurrency(draftTotal)}</span>
      </div>

      <div className="separator my-6" />

      <div className="d-flex justify-content-end gap-3 flex-wrap">
        {showCancel && (
          <button
            type="button"
            className="btn btn-light btn-sm"
            onClick={onCancel}
          >
            Cancelar
          </button>
        )}
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={handleConfirm}
          disabled={saving}
        >
          {saving
            ? "Registrando..."
            : openPedidoForWristband
              ? "Adicionar ao Pedido"
              : "Registrar Pedido"}
        </button>
      </div>
    </>
  );
};
